import {
  createContext,
  FC,
  PropsWithChildren,
  Suspense,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { graphql, useLazyLoadQuery } from "react-relay";
import { Id } from "../node";
import { useTradeOrderSubscriptions } from "../trade/TradeSubscriptions";
import {
  accountDataQuery,
  accountDataQuery$data,
  accountDataQuery$variables,
} from "./__generated__/accountDataQuery.graphql";
import { useWalletAccounts } from "./accounts";
import { balanceQueryAddresses } from "./balanceAddresses";
import { deploymentConfig, isV2Enabled } from "../redacted/v2/config";
import { useRedacted } from "./redacted";
import {
  accountDataQueryScope,
} from "./privateAccount";
import { accountDataRouteFlags } from "./accountDataRoute";

const one = graphql`
  query accountDataQuery(
    $id: ID
    $addresses: [Address!]!
    $loadAccount: Boolean!
    $loadAutoClaimer: Boolean!
    $loadBalancesPool: Boolean!
    $loadBorrow: Boolean!
    $loadIndex: Boolean!
    $loadLiquidate: Boolean!
    $loadMerge: Boolean!
    $loadPortfolio: Boolean!
    $loadStake: Boolean!
    $loadStrategies: Boolean!
    $loadTrade: Boolean!
  ) {
    node(id: $id) @include(if: $loadAccount) {
      ... on Account {
        ...HeaderAccountFragment
        ...BalanceContextFragment
        ...BalanceFragment @include(if: $loadMerge)
        ...BalancesPoolFragment @include(if: $loadBalancesPool)
        ...BorrowAccountFragment @include(if: $loadBorrow)
        ...DebtsAccountFragment @include(if: $loadLiquidate)
        ...GhostVaultAccountFragment @include(if: $loadStrategies)
        ...IndexAccountFragment @include(if: $loadIndex)
        ...IndexBalancesFragment @include(if: $loadIndex)
        ...MergingFragment @include(if: $loadMerge)
        ...OrderBookAccountFragment @include(if: $loadTrade)
        ...PortfolioFragment @include(if: $loadPortfolio)
        ...PositionsPortfolioFragment @include(if: $loadPortfolio)
        ...LimitOrdersAccountFragment @include(if: $loadTrade)
        ...RecurringOrdersAccountFragment @include(if: $loadTrade)
        ...RangeOrdersAccountFragment @include(if: $loadTrade)
        ...StakeAccountFragment @include(if: $loadStake)
        ...StakeOverviewAccountFragment @include(if: $loadStake)
        ...StrategyAccountFragment @include(if: $loadStrategies)
        ...ThorchainPoolAccountFragment @include(if: $loadStrategies)
        ...TradeSubscriptionsFragment @include(if: $loadTrade)
        ...useAutoClaimerFragment @include(if: $loadAutoClaimer)
      }
    }
  }
`;

const Context = createContext<{
  accountData?: accountDataQuery$data["node"];
  isPrivate: boolean;
  isLoading: boolean;
  loadTrade: boolean;
}>({
  accountData: undefined,
  isPrivate: false,
  isLoading: false,
  loadTrade: false,
});

export const AccountDataContext: FC<PropsWithChildren> = ({ children }) => {
  const { isPrivateMode, subWalletAddress, updateRedactedFlag } = useRedacted();
  // The public scope is the connected wallets'; a private scope never
  // includes them (accountDataQueryScope).
  const { selected, accounts } = useWalletAccounts();
  const { pathname } = useLocation();
  const addresses = useMemo(() => balanceQueryAddresses(accounts), [accounts]);
  const [pollKey, setPollKey] = useState(0);
  const [loaded, setLoaded] = useState<{
    variables: accountDataQuery$variables;
    accountData: accountDataQuery$data["node"];
  }>();
  const selectedAddress = selected?.address.address;
  const v2Enabled = isV2Enabled();
  const v2ProxyAddress = v2Enabled
    ? deploymentConfig().proxyContract
    : undefined;
  const {
    loadAutoClaimer,
    loadBalancesPool,
    loadBorrow,
    loadIndex,
    loadLiquidate,
    loadMerge,
    loadPortfolio,
    loadStake,
    loadStrategies,
    loadTrade,
  } = accountDataRouteFlags(pathname);

  const { accountAddress, queryAddresses } = useMemo(
    () =>
      accountDataQueryScope({
        selectedAddress,
        publicMetadataAddresses: addresses,
        isPrivateMode,
        subWalletAddress,
        proxyContract: v2ProxyAddress,
        v2Enabled,
      }),
    [
      addresses,
      isPrivateMode,
      selectedAddress,
      subWalletAddress,
      v2Enabled,
      v2ProxyAddress,
    ]
  );
  const variables = useMemo(
    () => ({
      ...(accountAddress
        ? { id: Id.account({ address: accountAddress }) }
        : {}),
      addresses: queryAddresses,
      loadAccount: !!accountAddress,
      loadAutoClaimer,
      loadBalancesPool,
      loadBorrow,
      loadIndex,
      loadLiquidate,
      loadMerge,
      loadPortfolio,
      loadStake,
      loadStrategies,
      loadTrade,
    }),
    [
      accountAddress,
      loadAutoClaimer,
      loadBalancesPool,
      loadBorrow,
      loadIndex,
      loadLiquidate,
      loadMerge,
      loadPortfolio,
      loadStake,
      loadStrategies,
      loadTrade,
      queryAddresses,
    ]
  );
  useEffect(() => {
    if (!isPrivateMode || !subWalletAddress) return;
    const interval = window.setInterval(
      () => setPollKey((current) => current + 1),
      10_000
    );
    return () => window.clearInterval(interval);
  }, [isPrivateMode, subWalletAddress]);

  const context = {
    isPrivate: isPrivateMode,
    loadTrade,
  };

  // Keep the application (especially open modals and wallet requests) mounted
  // once while Relay loads another account. A fallback containing `children`
  // creates a second application with independent modal state and stale closes.
  // Never expose the previous account's data while a new scope is loading.
  const isCurrent = loaded?.variables === variables;
  return (
    <Context.Provider
      value={{
        ...context,
        accountData: isCurrent ? loaded.accountData || undefined : undefined,
        isLoading: !isCurrent,
      }}>
      <Suspense fallback={null}>
        <AccountDataQuery
          variables={variables}
          fetchKey={`${Number(updateRedactedFlag)}:${pollKey}`}
          onLoaded={setLoaded}
        />
      </Suspense>
      {children}
    </Context.Provider>
  );
};

const AccountDataQuery: FC<{
    variables: accountDataQuery$variables;
    fetchKey: string;
    onLoaded: (result: {
      variables: accountDataQuery$variables;
      accountData: accountDataQuery$data["node"];
    }) => void;
}> = ({ variables, fetchKey, onLoaded }) => {
  const query = useLazyLoadQuery<accountDataQuery>(one, variables, {
    fetchKey,
    fetchPolicy: "store-and-network",
  });

  useLayoutEffect(() => {
    onLoaded({ variables, accountData: query.node });
  }, [onLoaded, query.node, variables]);

  return null;
};

/**
 * @param createSubscriptions Pass `false` to read account data without also
 * opening this caller's trade-order subscriptions.
 */
export const usePreloadedAccountData = (
  createSubscriptions = true
): {
  accountData?: accountDataQuery$data["node"];
  isLoading: boolean;
} => {
  const { accountData, isLoading, loadTrade } = useContext(Context);
  useTradeOrderSubscriptions(
    accountData || undefined,
    createSubscriptions && loadTrade
  );
  return { accountData, isLoading };
};
